import Image from 'next/image';
import Link from 'next/link';
import { parse, format } from 'date-fns';
import { CalendarCardProps } from './types';
import DateLabel from '../date/DateLabel';
import DefaultTeamLogo from '../../../../public/png/logo_round.png';
import { truncateString } from '@/lib/utils/string';

const getPeriodLabel = (period: string) => {
  const periodAsInt = parseInt(period, 10);

  if (periodAsInt === 1) {
    return '1er Quart';
  }

  if (periodAsInt <= 4) {
    return `${periodAsInt}e Quart`;
  } else {
    if (periodAsInt === 5) {
      return '1ère Prol.';
    } else {
      return `${periodAsInt - 4}nde Prol.`;
    }
  }
};

const getClock = (clock: string) => {
  try {
    return format(parse(clock, 'mm:ss:SSS', new Date()), 'mm:ss');
  } catch (e) {
    return '';
  }
};

export default function CardCalendarOrScore({
  callToActionUrl: { url, external, slug },
  currentPeriod,
  isoDate,
  quarterScores,
  title,
  team1,
  team2,
  live = false,
  dateLabelType = 'live',
  place = '',
  gsId,
  preparation = false,
  phase = '',
  journee = '',
  clock,
}: CalendarCardProps) {
  const link = external ? url : `${url}/${slug}`;
  const target = external ? '_blank' : '_self';
  const team1Logo = team1?.logo || DefaultTeamLogo;
  const team2Logo = team2?.logo || DefaultTeamLogo;
  const liveToCome = gsId && !live && isoDate && new Date(isoDate) > new Date();
  const cardContent = (
    <div className="flex h-full w-full flex-col items-center">
      <div className="border-gray-100/opacity-60 flex w-full justify-center gap-1 border-b">
        <span className="grow p-2">
          {title && <h3 className="grow px-2 text-xs font-semibold text-slate-950">{title}</h3>}
          {!title && preparation && (
            <h3 className="grow px-2 text-xs font-semibold text-slate-950">{`Match de préparation`}</h3>
          )}
          {!title && !preparation && phase && (
            <h3 className="grow px-2 text-xs font-semibold text-slate-950">{phase}{journee && <span className={`text-slate-500 text-gray-400 `}>{` | ${journee}`}</span>}</h3>
          )}
        </span>
        {isoDate && !live && (
          <DateLabel
            isoDate={isoDate}
            styles="text-gray-400 text-xs font-semibold p-2"
            type={dateLabelType}
          />
        )}
        {liveToCome && (
          <span className="flex items-center rounded-tr-md bg-gray-400 p-2 text-xs font-semibold uppercase text-white">
            <span className="mr-1 h-[10px] w-[10px] rounded-full bg-white" />
            Live
          </span>
        )}
        {live && (
          <span className="flex items-center rounded-tr-md bg-red-600 p-2 text-xs font-semibold uppercase text-white">
            <span className="mr-1 h-[10px] w-[10px] rounded-full bg-white" />
            Live
          </span>
        )}
      </div>
      <div className="mx-2 my-5 grid grid-cols-3 items-baseline justify-items-center gap-1">
        <div className="relative h-[45px] w-[45px]">
          <Image
            src={team1Logo}
            alt={''}
            fill={true}
            style={{
              objectFit: 'contain',
            }}
            sizes="45px"
          />
        </div>
        <div className="flex items-center justify-center gap-3">
          {(team1?.score || team1?.score === 0) && (
            <span
              className={`mt-1 font-AgencyFBBlackComp text-4xl antialiased ${+(team1?.score || 0) > +(team2?.score || 0) ? 'text-slate-950' : 'text-gray-400'}`}
            >
              {team1?.score}
            </span>
          )}
          <Image
            src={`/svg/versus.svg`}
            alt={''}
            width="34"
            height="30"
            className="self-center justify-self-center"
          />
          {(team2?.score || team2?.score === 0) && (
            <span
              className={`mt-1 font-AgencyFBBlackComp text-4xl antialiased ${+(team2?.score || 0) > +(team1?.score || 0) ? 'text-slate-950' : 'text-gray-400'}`}
            >
              {team2?.score}
            </span>
          )}
        </div>
        <div className="relative h-[45px] w-[45px]">
          <Image
            src={team2Logo}
            alt={''}
            fill={true}
            style={{
              objectFit: 'contain',
            }}
            sizes="45px"
          />
        </div>
        <div className="relative h-[20px] w-[100px]">
          <span className="relative flex justify-center text-center text-xs font-bold uppercase">
            {truncateString(team1?.name || '', 20)}
          </span>
        </div>
        <div className="flex w-full justify-between text-xs font-semibold">
          {live && (
            <>
              {currentPeriod && <span>{getPeriodLabel(currentPeriod)}</span>}
              {clock && <span>{getClock(clock)}</span>}
            </>
          )}
        </div>
        <div className="relative h-[20px] w-[100px]">
          <span className="relative flex justify-center text-center text-xs font-bold uppercase">
            {truncateString(team2?.name || '', 20)}
          </span>
        </div>
      </div>
      {(quarterScores || []).length > 0 && (
        <div
          className={`flex w-full items-center justify-center gap-2 border-t text-xs text-gray-500`}
        >
          {quarterScores?.map((score, index) => (
            <div
              key={index}
              className={`flex h-7 w-full items-center justify-center ${index < quarterScores.length - 1 ? 'border-gray-100/opacity-60 border-r' : ''}`}
            >
              {score[0]} - {score[1]}
            </div>
          ))}
        </div>
      )}
      {place && (!quarterScores || quarterScores.length === 0) && (
        <div
          className={`mt-auto flex h-7 w-full items-center justify-center gap-2 border-t text-xs text-gray-500`}
        >
          {place}
        </div>
      )}
    </div>
  );

  return (
    <div className="card relative flex h-full w-full flex-col rounded-md border border-gray-100 bg-white">
      {link && (
        <Link href={link} target={target} className={`h-full`}>
          {cardContent}
        </Link>
      )}
      {!link && <div className={`h-full`}>{cardContent}</div>}
    </div>
  );
}
